import React from 'react';
import TermsLayout from './TermsLayout';

function PrivacyPolicy() {
  const sections = [
    {
      title: "1. Introduction",
      content: `Zella is a workplace wellbeing platform consisting of a mobile survey application for employees and a web-based dashboard for company administrators. Our platform helps organisations monitor fatigue levels and overall wellbeing in the workplace through voluntary surveys.

We are committed to protecting your privacy and complying with UK GDPR and the Data Protection Act 2018. Where we process data relating to individuals in EU member states, we also comply with EU GDPR.

Data Controller (for account and billing data):
Ursell Property Limited (trading as Zella Fatigue & Wellbeing)
Carpenters House, 24 The Street, Leighterton, United Kingdom, GL8 8UN
Company number: 11056504
Email: privacy@zellawellbeing.com
Website: https://zellawellbeing.com

Carpenters House, 24 The Street, Leighterton, GL8 8UN
Company number: 11056504

Note on roles: For employee survey data collected through the platform, your employer (the Customer organisation) acts as data controller and Zella acts as data processor. This distinction is explained in Section 3 below.`
    },
    {
      title: "2. Who This Policy Applies To",
      content: `This privacy policy applies to three distinct groups:

Survey Users (Mobile App Users)
Employees who use the Zella mobile app to complete wellbeing surveys. Your survey responses are aggregated at team level — administrators see only weekly team averages, not individual responses.

Organisation Administrators (Web Dashboard Users)
Company administrators who use the web dashboard to view aggregated team-level analytics, manage team memberships, and approve employee access.

Billing Contacts
The individual or organisation responsible for payment of a Zella subscription. Typically the same person as the administrator.`
    },
    {
      title: "3. Data Controller and Processor Roles",
      content: `Zella operates in two distinct legal capacities depending on the type of data being processed. This distinction is fundamental to understanding your rights and who is responsible for what.

For Employee Survey and Account Data — Your Employer is the Controller:

When an organisation deploys Zella to its employees, the organisation (your employer) is the data controller for employee personal data. This means your employer determines the purposes and means of processing — for example, deciding to use Zella for workplace wellbeing monitoring, determining which teams participate, and deciding how to use the resulting analytics.

Zella acts as a data processor in this context, processing employee data on behalf of and under the instructions of your employer. The relationship between Zella and your employer is governed by our Data Processing Agreement.

As a data processor, Zella does not independently determine the lawful basis for processing your survey data — that is your employer's responsibility. Your employer should inform you of the lawful basis they rely on (commonly legitimate interests in workplace health and safety, or in some cases a legal obligation). If you have questions about why your employer is collecting this data, you should contact your employer or HR department.

For Account and Billing Data — Zella is the Controller:

Zella acts as an independent data controller for administrator account data (names, email addresses, login credentials), billing and payment information, and platform security and fraud prevention data. Zella determines the purpose and means of processing this data for its own business purposes and is directly responsible to you for its handling.

Summary of Roles:

Employee survey data: Employer = Controller, Zella = Processor
Employee account data (email, name, team assignment): Employer = Controller, Zella = Processor
Administrator account data: Zella = Controller
Billing data: Zella = Controller
Platform security data: Zella = Controller`
    },
    {
      title: "4. Legal Bases for Processing",
      content: `UK GDPR requires a valid legal basis for each processing activity. The following table sets out the legal bases Zella relies on as controller, and notes where the employer (as controller) determines the basis for employee data.

Processing Activity | Legal Basis | Who Determines This
Administrator account creation and management | Article 6(1)(b) — contract performance | Zella (as controller)
Billing and payment processing | Article 6(1)(b) — contract performance; Article 6(1)(c) — legal obligation (HMRC 7-year retention) | Zella (as controller)
Platform security and fraud prevention | Article 6(1)(f) — legitimate interests (protecting the platform and users) | Zella (as controller)
Rate limiting and abuse prevention | Article 6(1)(f) — legitimate interests | Zella (as controller)
Service communications (password resets, billing alerts) | Article 6(1)(b) — contract performance | Zella (as controller)
Employee survey data collection and aggregation | Determined by the employer as data controller. Commonly: Article 6(1)(f) legitimate interests (workplace health and safety), or Article 6(1)(c) legal obligation | Employer (as controller)
Employee account data (email, name, team assignment) | Determined by the employer as data controller | Employer (as controller)
Research data sharing (aggregated, anonymised) | Article 6(1)(f) — legitimate interests (advancing workplace safety research). Zella applies aggregation thresholds before sharing — see Section 7 | Zella (as controller of aggregated outputs)
Feedback and bug reports | Article 6(1)(f) — legitimate interests (improving the service) | Zella (as controller)

Note for Employees:
If your employer requires you to use Zella, the legal basis for processing is determined by your employer, not Zella. Consent is generally not the appropriate legal basis in an employment context because it may not be freely given. Your employer should explain the lawful basis they rely on in their own employee privacy notice. Contact your employer or HR department with questions about why this data is being collected.

Automated Decision-Making:
Zella does not make solely automated decisions that produce legal or similarly significant effects concerning individuals under Article 22 of UK GDPR. Tiredness scores and team averages are informational outputs provided to administrators — all employment, staffing, scheduling, and safety decisions remain with the organisation and its personnel.`
    },
    {
      title: "5. Information We Collect",
      content: `From Employee Mobile App Users:

Account Information (stored in Firebase Authentication and Firestore):
- Email address (for authentication and account recovery)
- Display name (optional)
- Password (managed by Firebase Authentication — never stored in plain text)
- Company affiliation (entered via company code at registration)
- Team assignment (assigned by your administrator)
- Account status (pending / approved / rejected) and timestamps
- Firebase Authentication metadata (account creation timestamp, last sign-in timestamp)
- Admin flag: false (custom claim in Firebase Authentication)

Survey Response Data (stored in Firestore surveys collection):
- Happiness rating (integer, 1–5)
- Overall tiredness rating (integer, 1–5)
- Reaction time in milliseconds (optional)
- Submission timestamp
- Your user ID (uid), team ID, and company ID

Note: Survey records are pseudonymised, not anonymised. They contain your user ID, which links to your account record. Zella's security rules prevent administrators from accessing individual survey records — they see only aggregated weekly team averages. See Section 8 for full details.

Feedback Data (stored in Firestore feedback collection):
- Your user ID (uid) and company ID
- Your feedback message or bug report text
- Feedback type and submission timestamp

Note on feedback visibility: Feedback is visible to Zella personnel for support and development purposes. Administrators can see feedback submitted by employees in their company but feedback records include the uid — administrators could in principle identify which employee submitted which feedback. If you wish to submit anonymous feedback, contact privacy@zellawellbeing.com instead.

From Company Administrators:

Account Information:
- Email address, display name
- Password (managed by Firebase Authentication)
- Company ID, admin flag
- Account creation and last sign-in timestamps

Company and Team Information:
- Organisation name, industry, region
- Company access code
- Team names and member counts
- Employee approval records

Billing and Payment Information:
- Card brand and last 4 digits (display only)
- Billing address, subscription tier, employee count
- Payment history and scheduled payment records
- Stripe customer ID, subscription ID, price ID

Full card numbers, CVCs, and expiry dates never touch Zella's servers. All card data is handled exclusively by Stripe.

Data Not Collected:
Zella does not collect location data, device identifiers, biometric data, browsing history, or free-text survey responses. All survey data is numerical ratings only.

Data Accuracy:
We rely on information supplied by users and organisations. We cannot guarantee that all data held is accurate, complete, or current. You can correct inaccurate account data at any time — see Section 10.`
    },
    {
      title: "6. How Your Data is Stored",
      content: `All Zella application data is stored in Firebase Firestore on Google Cloud Platform servers located in europe-west2 (London, United Kingdom). Core application data does not leave the United Kingdom as part of Zella's standard infrastructure.

Key Firestore Collections:

users/{userId} — Employee account data
admins/{adminId} — Administrator account data
companies/{companyId} — Company records
companies/{companyId}/teams/{teamId} — Team records
surveys/{surveyId} — Individual survey responses (includes uid, teamId, companyId)
teamAverages/{docId} — Weekly aggregated team averages (no individual uid)
billingLimits/{companyId} — Subscription and payment metadata
feedback/{feedbackId} — Feedback and bug reports (includes uid)
stripeProcessedEvents/{eventId} — Webhook idempotency records (no personal data)
Rate limiting collections — Action counts by user/admin ID, auto-deleted after 48 hours

Firebase Authentication stores: uid, email, display name, creation timestamp, last sign-in timestamp, and custom claims (admin status, company ID). Managed by Firebase/Google.

Payment Data:
Card metadata (brand and last 4 digits) stored in Firestore. Full card data stored and managed exclusively by Stripe.`
    },
    {
      title: "7. How We Use Your Information",
      content: `Uses of Account Data (Zella as controller):
- Authentication and account management
- Granting access to the correct company and team data
- Sending service communications (password resets, approval notifications, billing alerts)
- Security and fraud prevention

Uses of Survey Data (Employer as controller, Zella as processor):
- Computing aggregated weekly team averages (teamAverages collection)
- Providing team-level wellbeing analytics to company administrators
- Improving platform algorithms

Individual survey responses are never surfaced to administrators. The platform computes team averages and only exposes those averages.

Uses of Billing Data (Zella as controller):
- Processing subscription payments via Stripe
- Enforcing employee limits and access controls
- Sending billing notifications and failure alerts
- Maintaining financial records as required by HMRC (7-year retention)

Uses of Feedback Data (Zella as controller):
- Investigating and resolving bugs and product issues
- Improving the platform

Uses of Rate Limiting Data (Zella as controller):
- Preventing abuse and automated attacks
- Auto-deleted after 48 hours, not used for any other purpose

Research Data Sharing (Zella as controller of aggregated outputs):
Zella may share aggregated, anonymised data with research partners. Before any external sharing, Zella applies aggregation thresholds and disclosure controls to reduce re-identification risk, including:
- Minimum observation thresholds (datasets are not shared where any cell contains fewer than an appropriate minimum number of observations)
- Replacement of all company and team identifiers with industry category labels
- Generalisation of timestamps to month and year only
- Review for outliers or combinations of attributes that could identify specific organisations

Research datasets contain average scores at an industry/region/size level — not individual survey responses and not company-level data.

How Company Administrators Can Use Data:

Administrators can:
- View aggregated weekly team averages (average happiness, tiredness, reaction time per team)
- See team member lists (names and emails of approved employees)
- Approve or deny access requests
- View team trends over time
- Access feedback submitted by employees (note: feedback records include uid — see Section 5)

Administrators cannot:
- Access individual survey responses
- See which employee submitted which survey response
- View data from other companies`
    },
    {
      title: "8. Survey Data — Pseudonymisation and Access Controls",
      content: `How Survey Data Flows:

1. An employee submits a survey in the mobile app
2. A record is written to the Firestore surveys collection containing: uid, teamId, companyId, happiness, tirednessOverall, reactionTime, submittedAt
3. A Cloud Function periodically computes weekly team averages and writes to the teamAverages collection: companyId, teamId, weekStartDate, avgHappiness, avgTirednessOverall, avgReactionTime, surveyCount
4. Administrators access only the teamAverages collection — individual survey records are never accessible through any product interface

Pseudonymisation, Not Anonymisation:

Survey records contain your user ID (uid). This uid links to your account record which contains your name and email. Survey data is therefore pseudonymised — it is not possible to determine who submitted a survey without also accessing the account records. However, the data is not fully anonymised because the link exists at the database level.

Access Controls:

Zella's Firestore security rules are formally defined rules at the database layer that prevent administrators from reading the surveys collection directly. Administrators can read only the teamAverages and company/team data collections. This is enforced independently of application logic.

Minimum Team Size:

Teams must have at least 5 approved members before their data appears in team averages. This is a technical control. Organisations acknowledge that anonymity may be further affected by attendance patterns, operational knowledge, or workforce composition beyond Zella's control — see Terms of Service Section 7.

What Happens When You Delete Your Account:

Your account record is deleted, severing the link between your uid and your identity. Survey records may retain your uid as a technical artefact. Because the account no longer exists, re-identification from Zella's systems becomes effectively impossible. On specific written request, Zella will delete survey records associated with your uid within 30 days — see Section 10 and the Delete Account page.`
    },
    {
      title: "9. Research Data Sharing and Third-Party Processors",
      content: `We do not sell personal information. We do not share personal information (names, emails, user IDs, billing data) with research partners.

Research Data Sharing:

Zella may share aggregated datasets with research partners including academic researchers, public health institutions, workplace safety organisations, and industry analysts. Before sharing, all individual identifiers are removed and aggregation thresholds are applied. Research datasets contain only industry-level averages — not company data, not individual responses.

Recipients must sign a Data Sharing Agreement prohibiting re-identification, use for employment screening or insurance underwriting, and sharing with unauthorised parties. Where appropriate, Zella may require evidence of institutional ethics approval from academic researchers.

Sub-Processors (Third-Party Data Processors):

The following sub-processors handle data on Zella's behalf:

Firebase / Google Cloud Platform (Google LLC)
Role: All core infrastructure — Firestore database, Firebase Authentication, Cloud Functions, Cloud Storage
Data location: europe-west2 (London, UK)
Note: Certain support, security, or infrastructure operations by Google may involve international access by Google personnel, subject to Google's own access controls and the UK-US Data Bridge safeguards
Agreement: Google Cloud Data Processing Addendum
Details: https://cloud.google.com/terms/data-processing-addendum

Stripe, Inc.
Role: Payment card processing
Data: Card metadata and billing information only — no survey or employee data
Location: United States
Transfer mechanism: UK-US Data Bridge (Stripe certified)
Details: https://stripe.com/gb/privacy

Google LLC (reCAPTCHA)
Role: Bot prevention at company administrator registration only
Data: Device and interaction signals at the registration step only
Location: United States
Transfer mechanism: UK-US Data Bridge (Google certified)

Email Service Provider
Role: Transactional emails
Data: Email address and name
Details: Available on request

All sub-processors are bound by Data Processing Agreements.

Legal Disclosures:
We may disclose information if required by law, court order, or regulatory request. We will notify affected users unless legally prohibited from doing so.`
    },
    {
      title: "10. Your Rights and How to Exercise Them",
      content: `Rights Under UK GDPR:

Important Note on Employee Rights:
For employee survey and account data, your employer (as data controller) is the primary point of contact for data subject rights requests. Where Zella holds the data and can technically assist, we will do so on request from you or your employer. Where your employer acts as controller, your employer is ultimately responsible for responding to your request.

Right to Access (Article 15):
Request a copy of your personal data. For account data (email, name, team assignment, account status): contact privacy@zellawellbeing.com with subject "Data Access Request." We can also provide a copy of survey records associated with your uid.

Right to Deletion (Article 17):
Delete your account via the mobile app (Settings → Delete Account) or email privacy@zellawellbeing.com. Account data deleted within 30 days. To request deletion of survey records associated with your uid, include this in your request. Billing records retained 7 years under HMRC legal obligation (Article 17(3)(b) exemption). See the Delete Account page for full details.

Right to Rectification (Article 16):
Update display name in app settings or password via "Forgot Password." Email privacy@zellawellbeing.com for other corrections.

Right to Restriction of Processing (Article 18):
Email privacy@zellawellbeing.com to request limitation of processing.

Right to Data Portability (Article 20):
Request account data and survey records in JSON or CSV format.

Right to Object (Article 21):
Email privacy@zellawellbeing.com to object to processing based on legitimate interests.

Right to Withdraw Consent:
Where consent is relied upon (if applicable), delete your account to stop future data collection.

Right not to be Subject to Automated Decision-Making (Article 22):
Zella does not make solely automated decisions producing legal or similarly significant effects. This right does not currently apply to Zella's processing.

Right to Lodge a Complaint (Article 77):
Information Commissioner's Office (ICO)
Website: https://ico.org.uk
Phone: 0303 123 1113
Address: Wycliffe House, Water Lane, Wilmslow, Cheshire SK9 5AF

EU residents may also contact their local EU supervisory authority.

We will respond to all requests within 30 days. Complex requests may take up to 60 days, in which case we will notify you.`
    },
    {
      title: "11. Data Retention",
      content: `Account Data (users, admins, Firebase Authentication):
Retained while the account is active. Deleted within 30 days of account deletion.

Survey Records (surveys collection):
Retained for up to 5 years from the date of submission to support longitudinal workplace wellbeing analytics and service improvement. Records associated with a deleted account lose their account link on account deletion. On specific request, records associated with a specific uid are deleted within 30 days. We review retention requirements periodically and may delete or anonymise historical records when no longer required for these purposes.

Team Averages (teamAverages collection):
Retained indefinitely as aggregated operational data containing no individual identifiers.

Feedback Records:
Retained until resolved or until the associated account is deleted, then deleted within 30 days.

Rate Limiting Records:
Automatically deleted after 48 hours.

Billing and Payment Records:
Retained for 7 years from the date of each transaction (HMRC requirement). This retention obligation overrides any erasure request for these specific records under Article 17(3)(b).

Company and Team Records:
Retained while the company account is active, then deleted within 30 days of account closure.

Note on Retention Implementation:
Zella does not currently have fully automated deletion workflows for survey records or feedback records. Deletion on account closure is processed manually within the 30-day commitment. We are working to implement automated deletion tooling.`
    },
    {
      title: "12. Data Security",
      content: `Technical Measures:
- Encryption in Transit: TLS 1.3 (HTTPS) for all data
- Encryption at Rest: Provided by Google Cloud and Firebase infrastructure
- Authentication: Managed by Firebase Authentication (industry-standard password hashing)
- Access Control: Role-based access using Firebase custom claims (isAdmin flag, companyId)
- Firestore Security Rules: Formally defined rules at the database layer preventing administrators from accessing the surveys collection
- Firebase App Check: Cryptographic attestation preventing unauthorised API access
- Rate Limiting: Per-user action limits preventing abuse
- Google reCAPTCHA: Bot prevention at company registration
- PCI DSS Compliance: Card data handled exclusively by Stripe (Level 1 PCI DSS certified)

Data Breach Notification:
Where legally required, we will notify the ICO within 72 hours of becoming aware of a personal data breach posing a risk to data subjects' rights and freedoms, and will notify affected users without undue delay. Notification will include the nature of the breach, likely consequences, and measures taken.`
    },
    {
      title: "13. International Data Transfers",
      content: `Core Application Data (Firebase):
All Zella application data is stored in Firebase Firestore in europe-west2 (London, United Kingdom). Core application data does not leave the UK as part of our standard infrastructure.

Google Support Access:
Certain support, security, or infrastructure operations by Google personnel may involve international access to systems, subject to Google's own access controls, confidentiality obligations, and the UK-US Data Bridge safeguards under Google's Data Processing Addendum.

Payment Processing (Stripe):
Payment data is transferred to Stripe in the United States under the UK-US Data Bridge. No employee survey data is involved.

Google reCAPTCHA:
Registration signals processed by Google in the US under the UK-US Data Bridge. One-time event at registration only.

EU Users:
EU-to-UK transfers are covered by the European Commission's adequacy decision for the UK. EU customers requiring additional information about onward UK-to-US transfers by Zella's sub-processors may request copies of applicable Standard Contractual Clauses by emailing privacy@zellawellbeing.com.`
    },
    {
      title: "14. Children's Privacy",
      content: `Zella is designed for workplace use by adults aged 18 and over. We do not knowingly collect personal information from anyone under 18. Contact privacy@zellawellbeing.com immediately if you believe we have collected information from a minor.`
    },
    {
      title: "15. Special Provisions for Employees",
      content: `Your Relationship With Your Organisation:

Zella provides the technology platform. We do not:
- Determine who must use Zella (this is your employer's decision)
- Have visibility into how your employer uses team analytics
- Control employment, staffing, or safety decisions made using Zella data

Your employer decides:
- Whether to use Zella and which teams participate
- Whether participation is mandatory or voluntary
- How to use team-level analytics
- The lawful basis for collecting your data

Zella provides aggregated information only and does not control, direct, or participate in employment decisions. All employment decisions based on any Zella data remain solely your employer's responsibility.

Employment-Related Concerns:

If you have concerns about being required to use Zella, how your employer uses team data, or retaliation related to survey participation, contact your employer, HR department, or employee representative. These are employment matters between you and your employer.

Whistleblower Information:

If you believe your employer is misusing Zella data or using it for unlawful monitoring, you may contact:
- The Information Commissioner's Office (ICO): https://ico.org.uk
- An employment tribunal or labour board
- A workplace whistleblower service in your jurisdiction`
    },
    {
      title: "16. Business Transfers and Asset Sales",
      content: `In the event of a merger, acquisition, bankruptcy, or similar transaction, personal data may be transferred as a business asset. We will notify you at least 30 days before any transfer of personal data. You can delete your account before the transfer if you object. The acquiring entity must honour this privacy policy or obtain fresh consent. Personal data cannot be transferred without appropriate legal process and compliance with UK GDPR.`
    },
    {
      title: "17. Accountability and Governance",
      content: `We adhere to UK GDPR's seven data protection principles: lawfulness, fairness, and transparency; purpose limitation; data minimisation; accuracy; storage limitation; integrity and confidentiality; and accountability.

Our Data Protection Measures:
- Privacy by design: privacy considerations built into system architecture
- Data Processing Agreements with all sub-processors
- Record of Processing Activities maintained per UK GDPR Article 30
- Legal bases documented for all processing activities (see Section 4)
- Incident response procedures for data breaches

Data Protection Officer:
Not currently required at Zella's scale. When appointed, contact details will be published here. Privacy enquiries: privacy@zellawellbeing.com.`
    },
    {
      title: "18. Cookies and Tracking",
      content: `We use only essential authentication and security cookies. We do not use advertising, behavioural, or social media tracking cookies. Google reCAPTCHA is used at company registration only. For full details see our Cookie Policy.`
    },
    {
      title: "19. Changes to This Privacy Policy",
      content: `For significant changes affecting your rights, we will provide email notification and 30 days notice before changes take effect. For minor changes, we will update the "Last Updated" date. Contact privacy@zellawellbeing.com with any questions.`
    },
    {
      title: "20. Your California Privacy Rights (CCPA)",
      content: `California residents have rights under CCPA including the right to know what personal information we collect, the right to delete it, and the right to opt out of its sale. We do not sell personal information. Aggregated research datasets do not constitute personal information under CCPA.

To exercise CCPA rights: Email privacy@zellawellbeing.com with subject "CCPA Request." We will respond within 45 days.`
    },
    {
      title: "21. Contact Us",
      content: `Privacy and Data Protection:
Email: privacy@zellawellbeing.com

Suggested subject lines:
- "Data Access Request"
- "Account Deletion"
- "GDPR Rights Request"
- "Data Sharing Question"
- "Research Transparency Request"
- "Privacy Concern"
- "URGENT — Privacy Issue" (for time-sensitive matters — we aim to respond within 72 hours)

Response time: Within 30 days (up to 60 days for complex requests)

General Support: use website contact form
Business Enquiries: hello@zellawellbeing.com`
    }
  ];

  const summary = {
    title: "Plain Language Summary",
    content: `Who controls your data:
For employee survey and account data: your employer is the data controller — they decide why and how Zella is used. Zella processes this data on their behalf as a data processor.
For administrator account and billing data: Zella is the data controller.

What we collect:
- Your email, optional name, team and company assignment
- Survey responses: happiness (1–5), tiredness (1–5), optional reaction time — numbers only, no free text
- For paying customers: card brand and last 4 digits only

About your survey data:
Survey records contain your user ID at the database level (pseudonymised). Administrators cannot access individual survey records — they see only weekly team averages. When your account is deleted, the link to your identity is removed.

Legal basis for processing:
For employee survey data, the lawful basis is determined by your employer (commonly legitimate interests in workplace safety). Zella does not rely on consent for employee survey processing.

What administrators see:
Weekly team averages only. Not individual responses.

Zella does not make automated decisions:
All employment, staffing, and safety decisions remain with your employer. Scores are informational estimates only.

Your rights: access, deletion, rectification, portability, objection — email privacy@zellawellbeing.com.

Data location: UK (London). Payments via Stripe (US, UK-US Data Bridge).`
  };

  const footer = {
    version: "Version: 4.0",
    lastUpdated: "Last Updated: June 2026",
    governing: "Governing Law: UK GDPR, Data Protection Act 2018",
    tagline: "Zella — Workplace Wellbeing Monitoring",
  };

  return (
    <TermsLayout>
      <h1>Privacy Policy</h1>
      <p className="last-updated">{footer.lastUpdated}</p>

      {sections.map((section, index) => (
        <div key={index} style={{ marginBottom: '40px' }}>
          <h2 style={{ fontSize: '24px', fontWeight: 'bold', marginTop: '30px', marginBottom: '15px', color: '#333' }}>
            {section.title}
          </h2>
          <div style={{ whiteSpace: 'pre-line', lineHeight: '1.8', fontSize: '15px', color: '#555' }}>
            {section.content}
          </div>
        </div>
      ))}

      <div style={{ backgroundColor: '#f8f9fa', border: '2px solid #1ab02a', borderRadius: '8px', padding: '24px', marginTop: '40px', marginBottom: '40px' }}>
        <h2 style={{ fontSize: '24px', fontWeight: 'bold', marginBottom: '15px', color: '#032054' }}>
          {summary.title}
        </h2>
        <div style={{ whiteSpace: 'pre-line', lineHeight: '1.8', fontSize: '15px', color: '#333' }}>
          {summary.content}
        </div>
      </div>

      <div style={{ borderTop: '2px solid #dee2e6', paddingTop: '20px', marginTop: '40px', fontSize: '14px', color: '#6c757d' }}>
        <p><strong>{footer.version}</strong></p>
        <p><strong>{footer.governing}</strong></p>
        <p style={{ marginTop: '20px', fontSize: '16px', fontWeight: 'bold', color: '#333' }}>{footer.tagline}</p>
      </div>
    </TermsLayout>
  );
}

export default PrivacyPolicy;